/**
 * Draft state for the Portfolio Planner form.
 *
 * Ported unchanged from the user platform's PortfolioPlanner/usePlannerForm.ts.
 * Every interlock, default and validation rule below is the rulebook's, and the
 * payload it builds is byte-for-byte the one `POST portfolio_planner/generate/`
 * receives from the user platform — the admin acts on the investor's behalf,
 * it does not plan by different rules.
 *
 * The rulebook (page 2) requires the screen to recalculate as the user types,
 * so every badge here is derived locally from the same TVM maths the engine
 * uses — none of it is a network call. The four interlocks it specifies all
 * live in this hook so they stay atomic with the edit that triggers them:
 *
 *   • Shariah = Yes  →  the bond route is unticked and locked
 *   • a goal's expected return prefills from its segment until the user edits it
 *   • a liability's EMI and interest rate derive from each other, both ways
 *   • a Retirement goal's timeline drives the SWP start date
 *
 * Segment boundaries and default returns come from bootstrap `config`, never a
 * local constant, so the badges can't drift from the generated plan.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addMonths,
  emiFromRate,
  fvSIP,
  inflate,
  monthlyEquivalent,
  rateFromEMI,
  segmentForYears,
  sipForTarget,
  toISODate,
  totalInterest,
} from "../../../Helpers/portfolioPlanner/tvm";
import type {
  PlannerConfig,
  PlannerFrequency,
  PlannerGenerateRequest,
  PlannerGoalType,
  PlannerInvestorType,
  PlannerLiabilityType,
  PlannerPrefill,
  PlannerRiskProfile,
  PlannerSegment,
  PlannerTaxBracket,
} from "../../../Helpers/portfolioPlanner/types";

/* ─────────────────────────────── drafts ─────────────────────────────── */

export interface ClientDraft {
  name: string;
  ageY: string;
  ageM: string;
  type: PlannerInvestorType;
  risk: PlannerRiskProfile | null;
  shariah: boolean;
  firstTimer: boolean;
  taxBracket: PlannerTaxBracket;
  monthlySurplus: number;
  lumpsum: number;
  investBonds: boolean;
}

export interface GoalDraft {
  key: string;
  type: PlannerGoalType;
  name: string;
  startDate: string;
  years: string;
  months: string;
  costToday: number;
  expectedReturn: string;
  /** Once true, the segment prefill must never overwrite the field again. */
  returnTouched: boolean;
  inflation: string;
  sipFrequency: PlannerFrequency;
}

export interface LiabilityDraft {
  key: string;
  type: PlannerLiabilityType;
  name: string;
  outstanding: number;
  rate: string;
  emi: number;
  years: string;
  months: string;
  /** How long the loan has already been running. */
  elapsedYears: string;
  elapsedMonths: string;
  /** Blank uses the segment default return. */
  returnOverride: string;
  /** Which side of the EMI ⇄ rate link the user drives; the other is derived. */
  linkSource: "rate" | "emi" | null;
}

export interface SwpDraft {
  startDate: string;
  returnPct: string;
  inflationPct: string;
  lumpsum: number;
  pension: number;
  withdrawal: number;
  frequency: PlannerFrequency;
  /** Stops the Retirement-goal sync from overwriting a hand-picked date. */
  dateTouched: boolean;
}

/* ────────────────────────────── derived ─────────────────────────────── */

export interface GoalBadges {
  tenureYears: number;
  segment: PlannerSegment | null;
  segmentLabel: string;
  futureCost: number | null;
  /** Indicative: what the goal alone would need, ignoring any lumpsum. */
  indicativeSip: number | null;
  isComplete: boolean;
}

export interface LiabilityBadges {
  months: number;
  totalInterest: number | null;
  sip10: number | null;
  sip10FV: number | null;
  /** Extra SIP for the months already passed; null for a new loan. */
  catchUpSip: number | null;
  cancels: boolean;
  isComplete: boolean;
}

export interface PlannerTotals {
  goalCount: number;
  totalFutureCost: number;
  lumpsum: number;
  indicativeMonthlySip: number;
  /** Indicative SIP against the declared surplus; negative means a shortfall. */
  surplusGap: number;
}

/**
 * Withdrawal inflation is prefilled at 8% rather than the bootstrap
 * `swpDefault.inflationPct` of 6%, on instruction — goal inflation already
 * defaults to 8 and the two reading differently confused reviewers. The value
 * is always sent explicitly on the payload, so the engine uses what is on
 * screen and nothing drifts.
 */
const SWP_INFLATION_DEFAULT_PCT = 8;

/**
 * The engine rejects a decimal year: 2.4 is ambiguous, meaning 2y 4.8m in base
 * ten rather than the 2y 4m a reader assumes. Tenures are therefore sent as a
 * whole-integer pair, and the total is capped at 491 months (40y 11m).
 */
const MAX_TENURE_MONTHS = 491;
const whole = (value: string): number => Math.trunc(Number(value) || 0);
const totalMonthsOf = (years: string, months: string): number =>
  whole(years) * 12 + whole(months);

/**
 * 12 months or more is carried into years, so "1y 15m" reads back as "2y 3m".
 * Below 12 the fields are left exactly as typed, blank included, so a value
 * being keyed in is never rewritten under the cursor.
 */
const carryMonths = <T extends { years: string; months: string }>(row: T): T => {
  if (whole(row.months) < 12) return row;
  const total = totalMonthsOf(row.years, row.months);
  return {
    ...row,
    years: String(Math.floor(total / 12)),
    months: String(total % 12),
  };
};

const carryElapsed = (row: LiabilityDraft): LiabilityDraft => {
  if (whole(row.elapsedMonths) < 12) return row;
  const total = totalMonthsOf(row.elapsedYears, row.elapsedMonths);
  return {
    ...row,
    elapsedYears: String(Math.floor(total / 12)),
    elapsedMonths: String(total % 12),
  };
};

const SEGMENT_TERM: Record<PlannerSegment, string> = {
  Debt: "Short term",
  Hybrid: "Medium term",
  Equity: "Long term",
};

const newKey = (() => {
  let seed = 0;
  return () => {
    seed += 1;
    return `row-${seed}`;
  };
})();

export const makeGoalDraft = (
  startDate: string,
  inflationDefault?: number
): GoalDraft => ({
  key: newKey(),
  type: "Wealth Creation",
  name: "",
  startDate,
  years: "",
  months: "",
  costToday: 0,
  expectedReturn: "",
  returnTouched: false,
  // Spec A3: inflation is prefilled at 8 and editable — a real value, not a
  // placeholder, so what the advisor sees is what the plan uses.
  inflation: inflationDefault != null ? String(inflationDefault) : "",
  // Spec A3/B1: the rulebook has no per-goal SIP frequency field; the model
  // fixes it at Monthly. The API accepts all four if that ever changes.
  sipFrequency: "Monthly",
});

export const makeLiabilityDraft = (): LiabilityDraft => ({
  key: newKey(),
  type: "Home Loan",
  name: "",
  outstanding: 0,
  rate: "",
  emi: 0,
  years: "",
  months: "",
  elapsedYears: "",
  elapsedMonths: "",
  returnOverride: "",
  linkSource: null,
});

const numberOf = (value: string): number => Number(value) || 0;
/* Whole months over 12, never years + months / 12 on raw input: a "8.3" that
   got that far would cost the goal at 1.08^8.3 here while the request sent
   8 years. */
const tenureYears = (years: string, months: string): number =>
  totalMonthsOf(years, months) / 12;

export const usePlannerForm = (
  config: PlannerConfig | undefined,
  prefill: PlannerPrefill | undefined
) => {
  const today = useRef(toISODate(new Date())).current;

  const [client, setClientState] = useState<ClientDraft>(() => ({
    name: "",
    ageY: "",
    ageM: "",
    type: "Salaried",
    // The stored questionnaire has no scoring function, so this can only ever
    // be answered by the user — never prefilled.
    risk: null,
    shariah: false,
    firstTimer: false,
    taxBracket: 30,
    monthlySurplus: 0,
    lumpsum: 0,
    investBonds: false,
  }));
  const [goals, setGoals] = useState<GoalDraft[]>(() => [makeGoalDraft(today)]);
  const [liabilities, setLiabilities] = useState<LiabilityDraft[]>([]);
  const [swp, setSwpState] = useState<SwpDraft>(() => ({
    startDate: "",
    returnPct: "",
    inflationPct: "",
    lumpsum: 0,
    pension: 0,
    withdrawal: 0,
    frequency: "Monthly",
    dateTouched: false,
  }));

  /* Bootstrap prefill is applied once, and only over fields the user has not
     already filled in — bootstrap can land after typing has started and must
     never clobber it. */
  const prefillApplied = useRef(false);
  useEffect(() => {
    if (!prefill || !config || prefillApplied.current) return;
    prefillApplied.current = true;
    setClientState((current) => ({
      ...current,
      name: current.name || prefill.name || "",
      ageY: current.ageY || (prefill.ageYears != null ? String(prefill.ageYears) : ""),
      ageM: current.ageM || (prefill.ageMonths != null ? String(prefill.ageMonths) : ""),
      type: prefill.investorType ?? current.type,
      taxBracket: prefill.taxBracket ?? current.taxBracket,
      firstTimer: prefill.firstTimer ?? current.firstTimer,
    }));
    setSwpState((current) => ({
      ...current,
      returnPct: current.returnPct || String(config.swpDefault.returnPct),
      inflationPct:
        current.inflationPct || String(SWP_INFLATION_DEFAULT_PCT),
    }));
  }, [prefill, config]);

  const segmentOf = useCallback(
    (years: number): PlannerSegment | null =>
      config && years > 0 ? segmentForYears(years, config.segBoundaries) : null,
    [config]
  );

  /* ── SWP start date follows the Retirement goal's timeline ──
     An effect rather than part of the goal updater: a state updater that
     writes other state is impure, and React is free to run it twice. */
  useEffect(() => {
    if (swp.dateTouched) return;
    const retirement = goals.find((goal) =>
      goal.type.toLowerCase().includes("retire")
    );
    if (!retirement?.startDate) return;
    const months = totalMonthsOf(retirement.years, retirement.months);
    if (months <= 0) return;
    const startDate = toISODate(
      addMonths(new Date(`${retirement.startDate}T00:00:00`), months)
    );
    setSwpState((current) =>
      current.dateTouched || current.startDate === startDate
        ? current
        : { ...current, startDate }
    );
  }, [goals, swp.dateTouched]);

  /* ── client ── */
  const patchClient = useCallback((patch: Partial<ClientDraft>) => {
    setClientState((current) => {
      const next = { ...current, ...patch };
      if (whole(next.ageM) >= 12) {
        const total = whole(next.ageY) * 12 + whole(next.ageM);
        next.ageY = String(Math.floor(total / 12));
        next.ageM = String(total % 12);
      }
      // Shariah bars structured interest-bearing deployment, so the bond route
      // is unticked and stays locked while it is on.
      if (next.shariah) next.investBonds = false;
      return next;
    });
  }, []);

  /* ── goals ── */
  const patchGoal = useCallback(
    (key: string, patch: Partial<GoalDraft>) => {
      setGoals((current) =>
        current.map((goal) => {
          if (goal.key !== key) return goal;
          const merged = carryMonths({ ...goal, ...patch });
          if (patch.expectedReturn !== undefined) merged.returnTouched = true;
          // Re-prefill the return whenever the tenure moves it into another
          // segment — but only while the user has left the field alone.
          if (
            config &&
            !merged.returnTouched &&
            (patch.years !== undefined || patch.months !== undefined)
          ) {
            const segment = segmentOf(tenureYears(merged.years, merged.months));
            merged.expectedReturn = segment
              ? String(config.defaultReturn[segment])
              : "";
          }
          return merged;
        })
      );
    },
    [config, segmentOf]
  );

  /* Config lands after first paint, so any goal created before it arrives gets
     its inflation prefill here. Only blank fields are touched. */
  useEffect(() => {
    if (!config) return;
    setGoals((current) =>
      current.some((goal) => goal.inflation === "")
        ? current.map((goal) =>
            goal.inflation === ""
              ? { ...goal, inflation: String(config.inflationDefault) }
              : goal
          )
        : current
    );
  }, [config]);

  const addGoal = useCallback(() => {
    setGoals((current) => [
      ...current,
      makeGoalDraft(today, config?.inflationDefault),
    ]);
  }, [today, config]);

  const removeGoal = useCallback(
    (key: string) => {
      setGoals((current) => {
        const next = current.filter((goal) => goal.key !== key);
        // Never leave the form with no goal at all — one empty row is the
        // minimum state the plan can be built from.
        return next.length
          ? next
          : [makeGoalDraft(today, config?.inflationDefault)];
      });
    },
    [today, config]
  );

  /* ── liabilities ── */
  const patchLiability = useCallback(
    (key: string, patch: Partial<LiabilityDraft>) => {
      setLiabilities((current) =>
        current.map((liability) => {
          if (liability.key !== key) return liability;
          const merged = carryElapsed(carryMonths({ ...liability, ...patch }));

          if (patch.rate !== undefined) merged.linkSource = "rate";
          if (patch.emi !== undefined) merged.linkSource = "emi";

          const months = totalMonthsOf(merged.years, merged.months);
          const source =
            merged.linkSource ??
            (merged.rate ? "rate" : merged.emi ? "emi" : null);

          // Both sides are always kept in step, whichever one is being typed:
          // the engine accepts either and derives the other, and the live
          // "total interest" badge needs the EMI regardless.
          if (merged.outstanding > 0 && months > 0) {
            if (source === "rate" && numberOf(merged.rate) > 0) {
              merged.emi = Math.round(
                emiFromRate(merged.outstanding, numberOf(merged.rate), months)
              );
            } else if (source === "emi" && merged.emi > 0) {
              merged.rate = rateFromEMI(
                merged.outstanding,
                merged.emi,
                months
              ).toFixed(2);
            }
          }
          return merged;
        })
      );
    },
    []
  );

  const addLiability = useCallback(() => {
    setLiabilities((current) => [...current, makeLiabilityDraft()]);
  }, []);

  const removeLiability = useCallback((key: string) => {
    setLiabilities((current) => current.filter((row) => row.key !== key));
  }, []);

  /* ── SWP ── */
  const patchSwp = useCallback((patch: Partial<SwpDraft>) => {
    setSwpState((current) => {
      const next = { ...current, ...patch };
      if (patch.startDate !== undefined) next.dateTouched = true;
      return next;
    });
  }, []);

  /* ───────────────────────────── badges ────────────────────────────── */

  const goalBadges = useMemo<Record<string, GoalBadges>>(() => {
    const entries: Record<string, GoalBadges> = {};
    goals.forEach((goal) => {
      const years = tenureYears(goal.years, goal.months);
      const segment = segmentOf(years);
      const inflation = goal.inflation
        ? numberOf(goal.inflation)
        : (config?.inflationDefault ?? 8);
      const expectedReturn = goal.expectedReturn
        ? numberOf(goal.expectedReturn)
        : segment && config
          ? config.defaultReturn[segment]
          : 0;
      const isComplete = goal.costToday > 0 && years > 0;
      const futureCost = isComplete
        ? inflate(goal.costToday, inflation, years)
        : null;
      entries[goal.key] = {
        tenureYears: years,
        segment,
        segmentLabel: segment ? `${segment} · ${SEGMENT_TERM[segment]}` : "—",
        futureCost,
        indicativeSip:
          futureCost != null && expectedReturn > 0
            ? sipForTarget(
                futureCost,
                expectedReturn,
                Math.round(years * 12),
                goal.sipFrequency
              )
            : null,
        isComplete,
      };
    });
    return entries;
  }, [goals, config, segmentOf]);

  const liabilityBadges = useMemo<Record<string, LiabilityBadges>>(() => {
    const entries: Record<string, LiabilityBadges> = {};
    liabilities.forEach((liability) => {
      const months = totalMonthsOf(liability.years, liability.months);
      const isComplete =
        liability.outstanding > 0 && liability.emi > 0 && months > 0;
      if (!isComplete || !config) {
        entries[liability.key] = {
          months,
          totalInterest: null,
          sip10: null,
          sip10FV: null,
          catchUpSip: null,
          cancels: false,
          isComplete: false,
        };
        return;
      }
      /* Mirrors the engine's plan_liability: a loan already running adds the
         interest paid so far (at the same monthly pace) and a top-up SIP that
         rebuilds what the 10% SIP would have grown to since the loan began. */
      /* The EMI shown in the field is rounded to whole rupees, but the engine
         derives it from the rate unrounded — spec B16 test 4 expects total
         interest ₹23,18,685, which the rounded EMI misses by ₹115. Badge off
         the exact figure whenever the rate is what's driving the pair, so the
         live numbers match the generated plan. */
      const rate = numberOf(liability.rate);
      const exactEmi =
        liability.linkSource !== "emi" && rate > 0
          ? emiFromRate(liability.outstanding, rate, months)
          : liability.emi;
      const elapsed = totalMonthsOf(liability.elapsedYears, liability.elapsedMonths);
      const remaining = totalInterest(liability.outstanding, exactEmi, months);
      const interest = remaining + (remaining * elapsed) / months;
      const segment = segmentForYears(months / 12, config.segBoundaries);
      const assumedReturn = liability.returnOverride
        ? numberOf(liability.returnOverride)
        : config.defaultReturn[segment];
      const sip10 = exactEmi * config.liabilitySipFactor;
      const sip10FV = fvSIP(sip10, assumedReturn, months, "Monthly");
      const catchUpSip = elapsed
        ? sipForTarget(
            fvSIP(sip10, assumedReturn, elapsed, "Monthly"),
            assumedReturn,
            months,
            "Monthly"
          )
        : null;
      const plannedFV =
        catchUpSip != null
          ? fvSIP(sip10 + catchUpSip, assumedReturn, months, "Monthly")
          : sip10FV;
      entries[liability.key] = {
        months,
        totalInterest: interest,
        sip10,
        sip10FV,
        catchUpSip,
        cancels: plannedFV >= interest,
        isComplete: true,
      };
    });
    return entries;
  }, [liabilities, config]);

  const totals = useMemo<PlannerTotals>(() => {
    const complete = goals.filter((goal) => goalBadges[goal.key]?.isComplete);
    const totalFutureCost = complete.reduce(
      (sum, goal) => sum + (goalBadges[goal.key]?.futureCost ?? 0),
      0
    );
    // Indicative only: each goal is costed on its own, before the engine
    // applies the lumpsum to the longest one.
    const goalSip = complete.reduce(
      (sum, goal) =>
        sum +
        monthlyEquivalent(
          goalBadges[goal.key]?.indicativeSip ?? 0,
          goal.sipFrequency
        ),
      0
    );
    const liabilitySip = liabilities.reduce(
      (sum, row) =>
        sum +
        (liabilityBadges[row.key]?.sip10 ?? 0) +
        (liabilityBadges[row.key]?.catchUpSip ?? 0),
      0
    );
    const indicativeMonthlySip = goalSip + liabilitySip;
    return {
      goalCount: complete.length,
      totalFutureCost,
      lumpsum: client.lumpsum,
      indicativeMonthlySip,
      surplusGap: client.monthlySurplus - indicativeMonthlySip,
    };
  }, [goals, goalBadges, liabilities, liabilityBadges, client]);

  /* ─────────────────────────── validation ──────────────────────────── */

  const validation = useMemo(() => {
    const problems: string[] = [];
    if (!totals.goalCount) {
      problems.push("Add at least one goal with both a cost and a timeline.");
    }
    if (!client.risk) {
      problems.push("Choose a risk profile — aggressive or conservative.");
    }
    goals.forEach((goal) => {
      if (totalMonthsOf(goal.years, goal.months) > MAX_TENURE_MONTHS) {
        problems.push(
          `${goal.name.trim() || goal.type}: a timeline longer than 40 years 11 months cannot be planned.`
        );
      }
    });
    liabilities.forEach((liability) => {
      if (totalMonthsOf(liability.years, liability.months) > MAX_TENURE_MONTHS) {
        problems.push(
          `${liability.name.trim() || liability.type}: a tenure longer than 40 years 11 months cannot be planned.`
        );
      }
      if (
        totalMonthsOf(liability.elapsedYears, liability.elapsedMonths) >
        MAX_TENURE_MONTHS
      ) {
        problems.push(
          `${liability.name.trim() || liability.type}: the time already passed cannot be longer than 40 years 11 months.`
        );
      }
    });
    liabilities.forEach((liability) => {
      const months = totalMonthsOf(liability.years, liability.months);
      if (
        liability.outstanding > 0 &&
        months > 0 &&
        !numberOf(liability.rate) &&
        !liability.emi
      ) {
        problems.push(
          `Give ${liability.name || liability.type} either an interest rate or an EMI.`
        );
      }
    });
    return { problems, canGenerate: problems.length === 0 };
  }, [totals.goalCount, client.risk, goals, liabilities]);

  /* ──────────────────────────── payload ────────────────────────────── */

  const toRequest = useCallback((): PlannerGenerateRequest => {
    const includedGoals = goals
      .filter((goal) => goalBadges[goal.key]?.isComplete)
      .map((goal, index) => ({
        id: index + 1,
        type: goal.type,
        name: goal.name.trim() || goal.type,
        startDate: goal.startDate || today,
        years: whole(goal.years),
        months: whole(goal.months),
        costToday: goal.costToday,
        expectedReturn: goal.expectedReturn ? numberOf(goal.expectedReturn) : null,
        inflation: goal.inflation ? numberOf(goal.inflation) : null,
        sipFrequency: goal.sipFrequency,
      }));

    /* Send only the side of the pair the advisor actually drove, and let the
       engine derive the other (the API's own instruction, and what spec B13
       step 1 does). Sending both would hand the engine our rounded EMI and
       shift total interest by a hundred rupees or so against the acceptance
       test; sending 0 is the documented signal to derive. */
    const includedLiabilities = liabilities
      .filter((row) => liabilityBadges[row.key]?.isComplete)
      .map((row) => {
        const drivenByEmi = row.linkSource === "emi";
        return {
          type: row.type,
          name: row.name.trim() || row.type,
          outstanding: row.outstanding,
          rate: drivenByEmi ? 0 : numberOf(row.rate),
          emi: drivenByEmi ? row.emi : 0,
          years: whole(row.years),
          months: whole(row.months),
          elapsedYears: whole(row.elapsedYears),
          elapsedMonths: whole(row.elapsedMonths),
          returnOverride: row.returnOverride ? numberOf(row.returnOverride) : null,
        };
      });

    const request: PlannerGenerateRequest = {
      client: {
        name: client.name.trim() || "Client",
        ageY: whole(client.ageY),
        ageM: whole(client.ageM),
        type: client.type,
        risk: client.risk ?? "Aggressive",
        shariah: client.shariah,
        firstTimer: client.firstTimer,
        taxBracket: client.taxBracket,
        monthlySurplus: client.monthlySurplus,
        lumpsum: client.lumpsum,
        investBonds: client.shariah ? false : client.investBonds,
      },
      goals: includedGoals,
    };

    if (includedLiabilities.length) request.liabilities = includedLiabilities;
    // An SWP is only produced when a withdrawal or a lumpsum is present, so
    // sending an empty block just adds noise.
    if (swp.withdrawal > 0 || swp.lumpsum > 0) {
      request.swp = {
        startDate: swp.startDate || today,
        returnPct: swp.returnPct
          ? numberOf(swp.returnPct)
          : (config?.swpDefault.returnPct ?? 9),
        inflationPct: swp.inflationPct
          ? numberOf(swp.inflationPct)
          : SWP_INFLATION_DEFAULT_PCT,
        lumpsum: swp.lumpsum,
        pension: swp.pension,
        withdrawal: swp.withdrawal,
        frequency: swp.frequency,
      };
    }
    return request;
  }, [
    goals,
    goalBadges,
    liabilities,
    liabilityBadges,
    client,
    swp,
    config,
    today,
  ]);

  return {
    today,
    client,
    goals,
    liabilities,
    swp,
    goalBadges,
    liabilityBadges,
    totals,
    validation,
    patchClient,
    patchGoal,
    addGoal,
    removeGoal,
    patchLiability,
    addLiability,
    removeLiability,
    patchSwp,
    toRequest,
  };
};

export type PlannerFormApi = ReturnType<typeof usePlannerForm>;
